import { Anchor } from 'src/design/atoms/Anchor';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Title } from 'src/design/atoms/Title';
import { Table } from 'src/design/molecules/Table';
import { groupBy, sumBy } from 'lodash';
import React from 'react';
import { Modal } from 'src/design/molecules/Modal';
import { ChangeDto } from 'src/api/FiretrolApiClient';
import { useChangesList } from 'src/api/queries/changes/useChangeQueries';
import { Currency } from 'src/design/atoms/Currency';
import { ChangesTable } from 'src/routes/Jobs/Changes/components/ChangesTable';

interface ChangesSummaryProps {
  jobId: number;
}

type ChangeWithCalculatedFields = ChangeDto & {
  numCost: number;
  numSchedule: number;
};

export const ChangesSummary: React.FC<ChangesSummaryProps> = ({ jobId }) => {
  const { data, isLoading: loading } = useChangesList(jobId);
  const [selectedChanges, setSelectedChanges] = React.useState<
    ChangeWithCalculatedFields[]
  >([]);
  const [openDialog, setOpenDialog] = React.useState(false);

  const allChanges = React.useMemo<ChangeWithCalculatedFields[]>(
    () =>
      (data ?? []).map((d) => ({
        ...d,
        numCost: d.proposedAmount ?? 0,
        numSchedule: d.proposedDays ?? 0,
      })),
    [data],
  );
  const changes = React.useMemo(
    () => groupBy(allChanges, (c) => c.status),
    [allChanges],
  );

  const showList = (items: ChangeWithCalculatedFields[]) => {
    setSelectedChanges(items);
    setOpenDialog(true);
  };

  const hideList = () => {
    setSelectedChanges([]);
    setOpenDialog(false);
  };

  const renderLines = () => {
    return Object.keys(changes).map((key) => {
      const items = changes[key];
      const totalCost = sumBy(items, (c) => c.numCost);
      const totalSchedule = sumBy(items, (c) => c.numSchedule);
      return (
        <Table.Tr key={key}>
          <Table.Td>{key}</Table.Td>
          <Table.Td>
            <Anchor onClick={() => showList(items)}>{items.length}</Anchor>
          </Table.Td>
          <Table.Td>{<Currency value={totalCost} />}</Table.Td>
          <Table.Td>{totalSchedule} days</Table.Td>
        </Table.Tr>
      );
    });
  };

  const renderAllLines = () => {
    const totalCost = sumBy(allChanges, (c) => c.numCost);
    const totalSchedule = sumBy(allChanges, (c) => c.numSchedule);
    return (
      <Table.Tr>
        <Table.Td>All Changes</Table.Td>
        <Table.Td>
          <Anchor onClick={() => showList(allChanges)}>
            {allChanges.length}
          </Anchor>
        </Table.Td>
        <Table.Td>{<Currency value={totalCost} />}</Table.Td>
        <Table.Td>{totalSchedule} days</Table.Td>
      </Table.Tr>
    );
  };

  return (
    <Paper p="md" pos="relative">
      <LoadingOverlay visible={loading} />
      <Title order={5} mb="md">
        Changes
      </Title>

      {!loading && (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Count</Table.Th>
              <Table.Th>Cost</Table.Th>
              <Table.Th>Schedule</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {renderAllLines()}
            {renderLines()}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={openDialog} onClose={hideList} title="Changes" size="xl">
        <ChangesTable changes={selectedChanges} loading={false} openInNewTab />
      </Modal>
    </Paper>
  );
};
